import { useEffect, useEffectEvent, useState } from 'react';

/** Distance of the indicator from which letting go goes back, in pixels. */
export const SWIPE_BACK_THRESHOLD = 64;
const SWIPE_MAX = 96;
/** The indicator moves half as far as the finger, like pull to refresh. */
const RESISTANCE = 0.5;
/** Only a touch that starts this close to the left edge is a swipe back. */
const EDGE = 24;
const SLOP = 8;

/** Dialogs, sheets, popups and maps have their own gestures. */
const OWN_GESTURES = '[role="dialog"], [data-radix-popper-content-wrapper], .maplibregl-map';

/**
 * True in the app installed on the home screen of an iPhone or iPad. There iOS has no swipe back
 * of its own; in Safari and on Android the browser or the system already has one.
 */
export function isIosHomeScreenApp(): boolean {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Swipe from the left edge to go back (docs/ui/responsive.md, Gesten), only in the app installed
 * on iOS and only while the page has a level above it. Letting go past `SWIPE_BACK_THRESHOLD`
 * calls `onBack`. Listeners are passive; a swipe that turns vertical is scrolling and ends it.
 */
export function useEdgeSwipeBack(enabled: boolean, onBack: () => void): number {
  const [distance, setDistance] = useState(0);
  const back = useEffectEvent(onBack);

  useEffect(() => {
    if (!enabled || !isIosHomeScreenApp()) return;
    let start: { x: number; y: number } | null = null;
    let swiping = false;
    let current = 0;

    const reset = () => {
      start = null;
      swiping = false;
      current = 0;
    };

    const onStart = (event: TouchEvent) => {
      reset();
      const touch = event.touches[0];
      if (!touch || event.touches.length > 1 || touch.clientX > EDGE) return;
      if (document.body.hasAttribute('data-scroll-locked')) return;
      if (event.target instanceof Element && event.target.closest(OWN_GESTURES)) return;
      start = { x: touch.clientX, y: touch.clientY };
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (!swiping) {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        if (dx <= 0 || Math.abs(dy) > dx) {
          start = null;
          return;
        }
        swiping = true;
      }
      current = Math.min(Math.max(dx, 0) * RESISTANCE, SWIPE_MAX);
      setDistance(current);
    };

    const onEnd = () => {
      const release = swiping && current >= SWIPE_BACK_THRESHOLD;
      const swiped = swiping;
      reset();
      if (!swiped) return;
      setDistance(0);
      if (release) back();
    };

    const onCancel = () => {
      if (swiping) setDistance(0);
      reset();
    };

    const passive = { passive: true } as const;
    document.addEventListener('touchstart', onStart, passive);
    document.addEventListener('touchmove', onMove, passive);
    document.addEventListener('touchend', onEnd, passive);
    document.addEventListener('touchcancel', onCancel, passive);
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
      setDistance(0);
    };
  }, [enabled]);

  return distance;
}
